// =============================================================================
// domain/webhooks.ts — the Discord webhook slots.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html:49614` (the App pane) and its
// handler at `:49857` — semantics, not code (D3). `#85`.
//
// ── 🚨 THE FEATURE WAS UNREACHABLE, NOT MERELY UNPORTED ──
//
// `TradeDetail.tsx:435` already gates a "Post to Discord" button on
// `prefs.webhooks.tradeDetails` being non-empty — and nothing in this client
// could store one. So the button existed, was correct, and could never appear.
// That is worse than a missing feature: the code reads as done.
//
// ── 🚨 THE URL IS THE CREDENTIAL ──
//
// A Discord webhook URL needs no other auth: anyone holding it can post to that
// channel as your app, for ever, until the channel owner rotates it. So it is
// entered into a password field, and the copy on screen says what it is rather
// than calling it a "URL" and leaving the reader to find out.
//
// ⚠ AND THE OLD CLIENT'S COPY SAYS IT "stays local", WHICH IS NOT TRUE HERE and
// is doubtful there. This client stores it in `user_preferences.webhooks` — a
// row on the server, synced across the user's devices. Repeating a reassurance
// that the storage model contradicts is how a privacy claim becomes a lie.
// =============================================================================

export type WebhookKey = 'preMarketPlan' | 'tradeDetails';

export interface WebhookPurpose {
  key: WebhookKey;
  label: string;
  /** What triggers a post to this channel, in the user's terms. */
  description: string;
}

/**
 * ⚠ TWO SLOTS, NOT ONE SETTING. The point is routing: a trader posts their
 * pre-market plan to one channel and individual trades to another, and a single
 * URL would force both into the same room.
 */
export const WEBHOOK_PURPOSES: readonly WebhookPurpose[] = [
  {
    key: 'preMarketPlan',
    label: 'Pre-Market Plan',
    description: 'Posts the rendered plan as an image, with a date-stamped caption.',
  },
  {
    key: 'tradeDetails',
    label: 'Trade Details',
    description: 'Posts one trade — P&L, R, MAE/MFE, pattern, exit model and notes '
      + '— with its chart screenshots attached.',
  },
];

/**
 * Does this look like a Discord webhook URL?
 *
 * ⚠ `https` AND THE HOST ARE BOTH CHECKED, and the host list is the same one
 * `connect-src` in `public/_headers` allows. If those two ever disagree, the
 * loser is the user: a URL this function accepts and the browser then refuses
 * fails with a network error that reads as "Discord rejected it".
 *
 * ⚠ `discordapp.com` is the legacy host and still appears in URLs people copied
 * years ago. It redirects — and a redirect to a host `connect-src` does not
 * name is blocked exactly like the original request would be, so it is named in
 * both places rather than accepted here and blocked there.
 */
export function isDiscordWebhookUrl(url: string): boolean {
  return /^https:\/\/(discord|discordapp)\.com\/api\/webhooks\/\S+/.test(String(url ?? '').trim());
}

/**
 * The stored map with one slot changed.
 *
 * ⚠ RETURNS THE SAME OBJECT when nothing changed, so a caller can skip a write
 * that would only bump the row's timestamp — the identity check the tag editor
 * next door uses for the same reason.
 *
 * ⚠ SPREADS THE STORED MAP. `webhooks` is one JSONB-ish column holding every
 * slot; building `{ tradeDetails: url }` would not set one slot, it would delete
 * the other. Same shape as the account `extras` blob (D110).
 */
export function setWebhook(
  stored: Readonly<Record<string, string>> | null | undefined,
  key: WebhookKey,
  url: string,
): Record<string, string> {
  const base = (stored ?? {}) as Record<string, string>;
  const next = String(url ?? '').trim();
  if ((base[key] ?? '') === next) return base;
  return { ...base, [key]: next };
}

/** What is stored for a slot, or `''`. */
export function webhookFor(
  stored: Readonly<Record<string, string>> | null | undefined, key: WebhookKey,
): string {
  return String((stored ?? {})[key] ?? '').trim();
}
